import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import {
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  PatientContext,
  PatientDispatchContext,
} from "../../contexts/PatientContext.tsx";
import { ProjectContext } from "../../contexts/ProjectContext";
import { useFetch } from "../../services/hooks/useFetch";
import { projectsPrefix } from "../../services/ProjectsServices";
import { getDiffDaysMessage } from "../../utils/main";
import CloseArrowTooltip from "../CloseArrowTooltip";
import CodeListDataGrid from "../codelist/CodeListDataGrid";
import IMDRFCategorize from "./categorize/IMDRFCategorize";
import EmptyCard from "./EmptyCard";
import NotesTable from "./notes/NotesTable.tsx";
import OrderLevelDocumentsCard from "./notes/OrderLevelDocumentsCard";
import RelatedVisitModal from "./RelatedVisitModal";
import SkeletonCard from "./skeletons/SkeletonCard";
import FlowsheetDataTable from "./tables/FlowsheetDataTable";
import LabDataTable from "./tables/LabDataTable.jsx";

export default function VisitsDrawer({
  toggleDrawer,
  initialVisitId,
}: {
  toggleDrawer: () => void;
  initialVisitId: string;
}) {
  const [encounterData, setEncounterData] =
    useState<EncounterSearchResult | null>(null);
  const [relatedVisitModal, setRelatedVisitModal] = useState(false);
  const [categorizeModal, setCategorizeModal] = useState(false);
  const [mark, setMark] = useState<HTMLElement | null>(null);

  const {
    id,
    encounters: rawEncounters,
    procedureDate,
  } = useContext(PatientContext)!;
  const encounters: PatientEncounterSummary[] = useMemo(
    () => rawEncounters ?? [],
    [rawEncounters],
  );

  const dispatch = useContext(PatientDispatchContext);
  const {
    id: projectId,
    isLocked,
    codeOffsetEnabled,
    documentRepositoryEnabled,
  } = useContext(ProjectContext)!;

  const [visitId, setVisitId] = useState(initialVisitId);
  const [visitIndex, setVisitIndex] = useState(
    encounters.findIndex(
      (e: PatientEncounterSummary) => e.id === initialVisitId,
    ),
  );
  const visitCount = encounters.length;

  const {
    fetch: fetchEncounter,
    isLoading: isEncounterLoading,
    error: fetchEncounterError,
  } = useFetch<EncounterSearchResult>();

  useEffect(() => {
    if (fetchEncounterError) {
      console.log("Error: " + fetchEncounterError);
    }
  }, [fetchEncounterError]);

  function disablePrevArrow() {
    return visitIndex === 0;
  }

  function disableNextArrow() {
    return visitIndex === visitCount - 1;
  }

  function goPrevVisit() {
    const prevVisitIndex = visitIndex - 1;
    const prevVisit = encounters[prevVisitIndex];
    getEncounter(prevVisit.id);
  }

  function goNextVisit() {
    const nextVisitIndex = visitIndex + 1;
    const nextVisit = encounters[nextVisitIndex];
    getEncounter(nextVisit.id);
  }

  function closeVisitModal() {
    setRelatedVisitModal(false);
    setMark(null);
  }

  function closeCategorizeModal() {
    setCategorizeModal(false);
    setMark(null);
  }

  function handleVisitUpdate(data: EncounterSearchResult) {
    setEncounterData(data);

    const existingEncounter = encounters.find((e) => e.id === data.id);
    if (!existingEncounter) return;

    const updatedEncounter: PatientEncounterSummary = {
      ...existingEncounter,
      isRelated: data.isRelated,
      reason: data.reason ?? existingEncounter.reason,
    };

    dispatch({
      type: "encounterUpdate",
      encounter: updatedEncounter,
    });
  }

  function handleVisitModal(e: React.MouseEvent<HTMLElement>) {
    setMark(e.currentTarget);
    setRelatedVisitModal(true);
  }

  const getEncounter = useCallback(
    (encounterId: string) => {
      const curIndex = encounters.findIndex(
        (e: PatientEncounterSummary) => e.id === encounterId,
      );
      setVisitIndex(curIndex);
      setVisitId(encounterId);
      void fetchEncounter(
        `${projectsPrefix}/${projectId}/patients/${id}/encounters/${encounterId}`,
        {
          postResponseAction: (data: EncounterSearchResult | undefined) => {
            setEncounterData(data ?? null);
          },
        },
      );
    },
    [fetchEncounter, encounters, id, projectId],
  );

  function handleCategorizeModal(e?: React.MouseEvent<HTMLElement>) {
    setMark(e?.currentTarget ?? null);
    setCategorizeModal(true);
  }

  useEffect(() => {
    if (!isEncounterLoading && !fetchEncounterError && !encounterData) {
      getEncounter(initialVisitId);
    }
  }, [
    getEncounter,
    initialVisitId,
    fetchEncounterError,
    encounterData,
    isEncounterLoading,
  ]);

  const visitNameText = encounterData
    ? encounterData.indexProcedure
      ? "Index Procedure: " + new Date(procedureDate).getUTCFullYear()
      : encounterData.diffDays !== undefined
        ? getDiffDaysMessage(encounterData.diffDays)
        : undefined
    : undefined;

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={toggleDrawer}
      slotProps={{
        paper: {
          sx: { width: "min(100%, 1300px)" },
        },
      }}
    >
      <div className="drawer-internal-wrapper">
        <div className="visit-modal-header">
          <div className="card-header">
            <div className="modal-header-left">
              <IconButton
                onClick={toggleDrawer}
                sx={{ "&:hover": { bgcolor: "action.hover" } }}
              >
                <ChevronRightIcon />
              </IconButton>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                {(encounterData || isEncounterLoading) && (
                  <IconButton
                    onClick={goPrevVisit}
                    sx={{ "&:hover": { bgcolor: "action.hover" } }}
                    disabled={disablePrevArrow()}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                )}
                {visitNameText && (
                  <Typography variant="subtitle1" className={"visit-name-text"}>
                    {visitNameText}
                  </Typography>
                )}
                {(encounterData || isEncounterLoading) && (
                  <IconButton
                    onClick={goNextVisit}
                    sx={{ "&:hover": { bgcolor: "action.hover" } }}
                    disabled={disableNextArrow()}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                )}
                {encounterData && encounterData.isRelated !== null && (
                  <Chip
                    label={encounterData.isRelated ? "Related" : "Unrelated"}
                    size="small"
                    sx={{
                      borderRadius: 3,
                      color: "common.white",
                      bgcolor: encounterData.isRelated ? green[600] : red[600],
                    }}
                    onDelete={() => undefined}
                    deleteIcon={
                      <Tooltip title={encounterData.reason}>
                        <StickyNote2OutlinedIcon
                          fontSize="small"
                          style={{ color: "#F0F0F0" }}
                        />
                      </Tooltip>
                    }
                  />
                )}
              </Stack>
            </div>
            <div className="modal-header-right">
              <CloseArrowTooltip
                title={isLocked ? "This project is locked." : ""}
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    className="categorize-button"
                    onClick={handleCategorizeModal}
                    disabled={isLocked}
                  >
                    Categorize Patient
                  </Button>
                </span>
              </CloseArrowTooltip>
              <CloseArrowTooltip
                title={isLocked ? "This project is locked." : ""}
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ textTransform: "none" }}
                    id="Mark Visit Button"
                    onClick={handleVisitModal}
                    disabled={isLocked}
                  >
                    Mark Visit As
                  </Button>
                </span>
              </CloseArrowTooltip>
            </div>
            {categorizeModal && (
              <IMDRFCategorize
                anchorVertical={"center"}
                onClose={closeCategorizeModal}
                open={true}
                anchorEl={mark}
              />
            )}
          </div>
        </div>
        <Divider flexItem />
        <div className="modal-content-visits">
          <div className="modal-content-visits-left">
            {encounterData && (
              <div className={"encounter-type"}>
                <Typography className={"label"}>Visit ID:</Typography>
                <Typography className={"type"} variant={"body2"}>
                  {encounterData.encounterNumber}
                </Typography>
              </div>
            )}
            {encounterData && encounterData.encounterType && (
              <div className={"encounter-type"}>
                <Typography className={"label"}>Visit Type:</Typography>
                <Typography className={"type"} variant={"body2"}>
                  {encounterData.encounterType}
                </Typography>
              </div>
            )}
            <Card className="visit-drawer-card">
              <CardContent className="clinical-notes">
                <Typography
                  variant="subtitle1"
                  className="section-header-title"
                >
                  Patient Data
                </Typography>
                <div className="clinical-notes-insight-items">
                  <div className={"clinical-notes-insight-item"}>
                    <Typography className={"visit-card-label"} variant="body2">
                      Allergies
                    </Typography>
                    <Typography variant="body2">
                      {((encounterData && encounterData.allergies) || []).join(
                        ", ",
                      )}
                    </Typography>
                  </div>
                </div>
              </CardContent>
            </Card>
            {documentRepositoryEnabled && (
              <OrderLevelDocumentsCard categorize={handleCategorizeModal} />
            )}
            <Card className="visit-drawer-card">
              <CardContent className="diagnosis-codes">
                <Typography variant="subtitle1">
                  <span className="section-header-title">Diagnoses</span>
                  <span className="section-header-subtitle">
                    &nbsp;for this visit
                  </span>
                </Typography>
                {(!encounterData || !encounterData.diagnosisCodes) && (
                  <SkeletonCard name="empty-data" />
                )}
                {encounterData &&
                  encounterData.diagnosisCodes &&
                  encounterData.diagnosisCodes.length > 0 && (
                    <CodeListDataGrid
                      codes={encounterData.diagnosisCodes}
                      isCodeOffsetEnabled={codeOffsetEnabled ?? false}
                      tableId="visits-diagnosis-codes"
                    />
                  )}
                {encounterData &&
                  encounterData.diagnosisCodes &&
                  encounterData.diagnosisCodes.length === 0 && (
                    <EmptyCard type="Diagnoses" />
                  )}
              </CardContent>
            </Card>
            <Card className="visit-drawer-card">
              <CardContent className="procedure-codes">
                <Typography variant="subtitle1">
                  <span className="section-header-title">Procedures</span>
                  <span className="section-header-subtitle">
                    &nbsp;for this visit
                  </span>
                </Typography>
                {(!encounterData || !encounterData.procedureCodes) && (
                  <SkeletonCard name="empty-data" />
                )}
                {encounterData &&
                  encounterData.procedureCodes &&
                  encounterData.procedureCodes.length > 0 && (
                    <CodeListDataGrid
                      codes={encounterData.procedureCodes}
                      isCodeOffsetEnabled={codeOffsetEnabled ?? false}
                      tableId="visits-procedure-codes"
                    />
                  )}
                {encounterData &&
                  encounterData.procedureCodes &&
                  encounterData.procedureCodes.length === 0 && (
                    <EmptyCard type="Procedures" />
                  )}
              </CardContent>
            </Card>
            <FlowsheetDataTable
              encounterId={visitId}
              countTotal={encounterData?.flowsheetCount?.total}
              highlightTotal={encounterData?.flowsheetCount?.highlight}
              eagerLoad
            />
            <LabDataTable
              encounterId={visitId}
              countTotal={encounterData?.labCount?.total}
              highlightTotal={encounterData?.labCount?.highlight}
              eagerLoad
            />
          </div>
          {encounterData ? (
            <NotesTable
              categorize={handleCategorizeModal}
              visitId={visitId}
              visitNameText={visitNameText}
              encounterNotesCount={encounterData.noteCount?.total || 0}
              encounterNumber={encounterData.encounterNumber}
              newNotesCount={encounterData.noteCount?.highlight || 0}
            />
          ) : (
            <SkeletonCard name="visit-drawer-card" />
          )}
        </div>
        {relatedVisitModal && encounterData && (
          <RelatedVisitModal
            anchorEl={mark}
            onClose={closeVisitModal}
            visit={encounterData}
            handleVisitUpdate={handleVisitUpdate}
          />
        )}
      </div>
    </Drawer>
  );
}
